import { setBodyClass, validateFormField } from '../helper/helpers';

export default class Register {
  constructor() {
    const register = document.getElementById('register');

    if (!register) {
      return;
    }

    setBodyClass('is-scroll-disabled', true);

    this.register = register;

    this.index = 0;
    this.currentStep = null;
    this.fieldsRequired = [];
    this.fieldsTrigger = [];

    this.form = register.querySelector('form.form');
    this.wizardItems = register.querySelectorAll('.wizard .item');
    this.btnStart = register.querySelector('button.btn-start');
    this.btnBack = register.querySelector('button.back');
    this.btnBackConfirm = register.querySelector('button.confirm-back');
    this.btnForward = register.querySelector('button.forward');
    this.btnSubmit = register.querySelector('button[type="submit"]');

    this.stepsSlider = register.querySelector('.steps');
    this.steps = register.querySelectorAll('.step');

    this.btnsOptional = register.querySelectorAll('.btn-optional');
    this.confirmCheckboxes = register.querySelectorAll(
      '.confirm-checkboxes input[required]',
    );

    this.currentWizardItem = this.wizardItems[0];

    this.btnBack.addEventListener('click', () => this.goTo(this.index - 1));
    this.btnBackConfirm.addEventListener('click', () =>
      this.goTo(this.steps.length - 1),
    );
    this.btnForward.addEventListener('click', () => this.goTo(this.index + 1));

    this.btnBack.setAttribute('disabled', true);

    this.btnStart.addEventListener('click', this.startMultistep);
    this.btnSubmit.addEventListener('click', this.submitMultistep);

    this.btnsOptional.forEach((btn) =>
      btn.addEventListener('click', () => this.toggleOptionalContent(btn)),
    );

    window.addEventListener('keyup', (e) => this.handleKeyup(e));

    this.validateConsent();
    this.confirmCheckboxes.forEach((cb) =>
      cb.addEventListener('change', this.validateConsent),
    );
  }

  /**
   * Toggles the visibility of an 'optional-content' section.
   * @param {Element} btn
   */
  toggleOptionalContent(btn) {
    const content = btn.nextElementSibling;

    if (btn.classList.contains('is-open')) {
      btn.classList.remove('is-open');
      content.classList.remove('is-open');
      content.style.maxHeight = 0;
    } else {
      btn.classList.add('is-open');
      content.classList.add('is-open');
      content.style.maxHeight = `${content.scrollHeight}px`;

      const stepInner = content.closest('.step-inner');

      stepInner.scrollTo({
        top: stepInner.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Starts the multistep form.
   */
  startMultistep = () => {
    this.register.classList.add('started');
    this.currentStep = this.steps[0];
  };

  /**
   * Submits the form, shows the success screen.
   */
  submitMultistep = () => {
    this.register.classList.remove('confirm');
    this.register.classList.add('success');
    this.form.submit();
  };

  /**
   * Changes the current step of the form, calls the 'updateValidation' method.
   * @param {Number} index
   */
  goTo(index) {
    this.index = index;
    this.stepsSlider.style.transform = `translateX(-${this.index * 25}%)`;

    if (this.currentWizardItem) {
      this.currentWizardItem.classList.remove('active');
    }

    if (this.index === 0) {
      this.btnBack.setAttribute('disabled', true);
    } else {
      this.btnBack.removeAttribute('disabled');
    }

    if (this.index >= this.steps.length) {
      this.register.classList.add('confirm');

      return;
    }

    this.register.classList.remove('confirm');
    this.currentStep = this.steps[this.index];
    this.currentWizardItem = this.wizardItems[this.index];
    this.currentWizardItem.classList.remove('disabled');
    this.currentWizardItem.classList.add('active');

    this.updateValidation();
  }

  /**
   * Updates the validation event-listeners. 'fieldsTrigger' contains all inputs, which when changed, will call the
   * validation. 'fieldsRequired' contains all inputs, which will be validated.
   */
  updateValidation() {
    this.fieldsTrigger.forEach((el) => {
      if (['text', 'email'].includes(el.type)) {
        el.removeEventListener('input', this.validateFields);
      } else if (['checkbox'].includes(el.type)) {
        el.removeEventListener('change', this.validateFields);
      }
    });

    this.fieldsRequired = this.currentStep.querySelectorAll('[required]');
    this.fieldsTrigger = this.currentStep.querySelectorAll(
      '[required], [data-trigger]',
    );

    this.fieldsTrigger.forEach((el) => {
      if (['text', 'email'].includes(el.type)) {
        el.addEventListener('input', this.validateFields);
      } else if (['checkbox', 'radio'].includes(el.type)) {
        el.addEventListener('change', this.validateFields);
      }
    });

    this.validateFields();
  }

  /**
   * Validates all required form fields of the current step. If the validation fails, the forward-button will be
   * disabled.
   */
  validateFields = () => {
    let isValid = true;

    Array.from(this.fieldsRequired).forEach((el) => {
      const radioParent = el.closest('.form-field__wrapper--radio');

      if (
        radioParent &&
        !radioParent.querySelector('input[type="radio"]:checked')
      ) {
        return;
      }

      if (!validateFormField(el)) {
        isValid = false;
      }
    });

    if (isValid) {
      this.currentStep.classList.remove('invalid');
      this.btnForward.removeAttribute('disabled');
    } else {
      this.currentStep.classList.add('invalid');
      this.btnForward.setAttribute('disabled', true);
    }
  };

  /**
   * Checks if all required consent checkboxes are selected (on the confirm page of the form).
   */
  validateConsent = () => {
    const isInvalid = Array.from(this.confirmCheckboxes).some(
      (cb) => !cb.checked,
    );

    this.btnSubmit.classList.toggle('disabled', isInvalid);
  };

  /**
   * Handles the 'key-up' behaviour. If "Enter" or "Backspace" is pressed by the user, an action will be executed,
   * depending on the current state
   * @param {Event} e
   */
  handleKeyup(e) {
    switch (e.key) {
      case 'Enter':
        if (!this.register.classList.contains('started')) {
          this.startMultistep();
          return;
        }

        if (
          this.register.classList.contains('confirm') &&
          !this.btnSubmit.hasAttribute('disabled')
        ) {
          this.submitMultistep();
          return;
        }

        if (!this.btnForward.hasAttribute('disabled')) {
          this.goTo(this.index + 1);
        }

        return;

      case 'Backspace':
        if (
          this.btnBack.hasAttribute('disabled') ||
          document.activeElement.tagName === 'INPUT'
        ) {
          // Checks
          return;
        }

        this.goTo(this.index - 1);
    }
  }
}
